import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Shield, ShieldOff, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Edit2, BookKey } from "lucide-react";
import { deleteUserFn } from "@/lib/admin-users.functions";
import { toggleResourceAccessFn, toggleRoleFn } from "@/lib/admin-permissions.functions";

export const Route = createFileRoute("/_authenticated/admin/users")({
  component: UsersAdmin,
});

interface Profile {
  id: string;
  full_name: string | null;
  phone: string | null;
  created_at: string;
  roles: string[];
  hasResourcesAccess?: boolean;
}

function UsersAdmin() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState<Profile | null>(null);

  const load = async () => {
    setLoading(true);
    const [{ data: profiles }, { data: roles }, { data: perms }] = await Promise.all([
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("user_roles").select("user_id,role"),
      supabase.from("user_permissions").select("user_id,can_access_resources"),
    ]);
    const rolesByUser = new Map<string, string[]>();
    (roles ?? []).forEach((r) => {
      const arr = rolesByUser.get(r.user_id) ?? [];
      arr.push(r.role);
      rolesByUser.set(r.user_id, arr);
    });

    const permsByUser = new Map<string, boolean>();
    (perms ?? []).forEach((p) => {
      permsByUser.set(p.user_id, p.can_access_resources);
    });

    setUsers(
      (profiles ?? []).map((p) => ({
        ...p,
        roles: rolesByUser.get(p.id) ?? [],
        hasResourcesAccess: permsByUser.get(p.id) ?? false,
      })),
    );
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggleResourceAccess = async (userId: string, currentStatus: boolean) => {
    setIsSubmitting(true);
    try {
      await toggleResourceAccessFn({ data: { targetUserId: userId, canAccess: !currentStatus } });
      toast.success(`Resources access ${!currentStatus ? "granted" : "revoked"}`);
      load();
    } catch (err: any) {
      toast.error(err.message || "Failed to change resource access");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRole = async (
    userId: string,
    role: "admin" | "parent" | "student" | "instructor",
    hasRole: boolean,
    currentRoles: string[],
  ) => {
    setIsSubmitting(true);
    try {
      await toggleRoleFn({ data: { targetUserId: userId, role, hasRole, currentRoles } });
      toast.success(hasRole ? `${role} role removed` : `Promoted to ${role}`);
      load();
    } catch (err: any) {
      toast.error(err.message || "Failed to change role");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email"));
    const password = String(fd.get("password"));
    const full_name = String(fd.get("fullName"));
    const role = String(fd.get("role"));

    setIsSubmitting(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name, role } },
    });
    setIsSubmitting(false);

    if (error) return toast.error(error.message);
    toast.success("User added successfully!");
    import("@/lib/logger").then((m) =>
      m.logActivity("Add User", `Added user ${full_name} (${email}) as ${role}`),
    );
    setOpen(false);
    load();
  };

  const handleEditUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingUser) return;
    const fd = new FormData(e.currentTarget);
    const full_name = String(fd.get("fullName"));
    const phone = fd.get("phone") ? String(fd.get("phone")) : null;

    setIsSubmitting(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        full_name,
        phone,
      })
      .eq("id", editingUser.id);
    setIsSubmitting(false);

    if (error) return toast.error(error.message);
    toast.success("User updated successfully!");
    import("@/lib/logger").then((m) =>
      m.logActivity("Edit User", `Updated profile for ${editingUser.id}`),
    );
    setEditingUser(null);
    load();
  };

  const handleDeleteUser = async (userId: string, userName: string | null) => {
    if (
      !confirm(
        `Are you sure you want to completely delete the user: ${userName || userId}? This action cannot be undone.`,
      )
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteUserFn({ data: { targetUserId: userId } });
      toast.success("User deleted successfully");
      import("@/lib/logger").then((m) =>
        m.logActivity("Delete User", `Deleted user ${userName || userId}`),
      );
      load();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-extrabold">Users</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary">
              <Plus className="h-4 w-4 mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input id="fullName" name="fullName" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="email" type="email" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" name="password" type="password" required minLength={6} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select name="role" defaultValue="student">
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={isSubmitting} className="w-full rounded-full mt-2">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Create
                User
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => {
            const isAdmin = u.roles.includes("admin");
            return (
              <Card key={u.id} className="rounded-2xl border-0 shadow-soft">
                <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <h3 className="font-bold">{u.full_name || "(no name)"}</h3>
                    <p className="text-xs text-muted-foreground">{u.phone || "—"}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {u.roles.map((r) => (
                        <Badge key={r} variant={r === "admin" ? "default" : "secondary"}>
                          {r}
                        </Badge>
                      ))}
                      {u.hasResourcesAccess && (
                        <Badge variant="outline" className="text-primary border-primary">
                          Resources
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => setEditingUser(u)}
                      className="rounded-full h-8 w-8"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => handleDeleteUser(u.id, u.full_name)}
                      className="rounded-full h-8 w-8"
                      disabled={isSubmitting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="rounded-full">
                          Manage Roles
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {(["admin", "instructor", "parent", "student"] as const).map((r) => (
                          <DropdownMenuItem
                            key={r}
                            onClick={() => toggleRole(u.id, r, u.roles.includes(r), u.roles)}
                          >
                            {u.roles.includes(r) ? (
                              <ShieldOff className="h-4 w-4 mr-2" />
                            ) : (
                              <Shield className="h-4 w-4 mr-2" />
                            )}
                            {u.roles.includes(r) ? `Revoke ${r}` : `Make ${r}`}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuItem
                          onClick={() => handleToggleResourceAccess(u.id, !!u.hasResourcesAccess)}
                        >
                          <BookKey className="h-4 w-4 mr-2 text-primary" />
                          {u.hasResourcesAccess
                            ? "Revoke Resources Access"
                            : "Grant Resources Access"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(o) => !o && setEditingUser(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleEditUser} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-fullName">Full Name</Label>
                <Input
                  id="edit-fullName"
                  name="fullName"
                  defaultValue={editingUser.full_name || ""}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input id="edit-phone" name="phone" defaultValue={editingUser.phone || ""} />
              </div>
              <DialogFooter className="mt-2">
                <Button type="submit" disabled={isSubmitting} className="w-full rounded-full">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
                  Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
